
import React, { useEffect, useMemo, useState } from 'react';
import { useProductNavigation } from '../../shared/hooks/useProductNavigation';
import './ViewProducts.css';
import { useParams, useSearchParams } from 'react-router-dom';
import { useCart } from '../../contexts/CartContext';
import Header from '../index/components/Header/Header';
import ViewAllSidebar, { Subcategory } from '../../shared/components/ViewAllComponent/ViewAllSidebar';
import ViewAllCards from '../../shared/components/ViewAllComponent/ViewAllCards';
import subcategoryProductService, { Product } from './services/product.service';
import categoryTreeService, { CategoryTreeNode } from '../ViewAll/services/categoryTree.service';
import './styles/ViewProducts.css';
import NavigationMobileMenu from '@/shared/components/NavigationMobileMenu/NavigationMobileMenu';
import FlotingCart from '../index/components/Header/floatingcart';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const ViewProducts: React.FC = () => {
  const navigateToProduct = useProductNavigation();
  const { slug } = useParams<{ slug: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [subcategories, setSubcategories] = useState<Subcategory[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [activeSubcategory, setActiveSubcategory] = useState<string | undefined>(slug);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [categoryName, setCategoryName] = useState<string>('');
  const { addToCart } = useCart();
  const [sort, setSort] = useState<string>(searchParams.get('sort') || 'new');

  const getTruncatedTitle = (title?: string, maxWords: number = 6) => {
    const safeTitle = (title || '').trim() || 'Item';
    const words = safeTitle.split(/\s+/);
    if (words.length <= maxWords) return safeTitle;
    return words.slice(0, maxWords).join(' ') + '...';
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const treeRes = await categoryTreeService.getCategoryTree();
        if (treeRes.success && treeRes.data) {
          let foundCategory: CategoryTreeNode | undefined;
          for (const cat of treeRes.data) {
            if (cat.children.some(sub => sub.slug === slug)) {
              foundCategory = cat;
              break;
            }
            if (cat.slug === slug) {
              foundCategory = cat;
              break;
            }
          }
          if (foundCategory) {
            setCategoryName(foundCategory.name);
            setSubcategories(foundCategory.children.map(sub => ({
              _id: sub._id,
              name: sub.name,
              slug: sub.slug,
              image: sub.image,
            })));
            let subSlug = slug;
            let activeSlug: string | undefined = undefined;
            if (foundCategory.children.some(sub => sub.slug === slug)) {
              activeSlug = slug;
              subSlug = slug;
            } else {
              // slug is category, fetch all products
              subSlug = slug;
              activeSlug = undefined;
            }
            setActiveSubcategory(activeSlug);
            if (subSlug) {
              const prodRes = await subcategoryProductService.getProductsByCategory(subSlug, 1000);
              if (prodRes.success && prodRes.data) {
                setProducts(prodRes.data);
              } else {
                setProducts([]);
              }
            }
          } else {
            setError('Category or subcategory not found');
          }
        } else {
          setError('Failed to load category tree');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Error fetching data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug]);

  // memoized sorted products
  const sortedProducts = useMemo(() => {
    const list = [...products];
    switch (sort) {
      case 'price_desc':
        return list.sort((a, b) => (b.price || 0) - (a.price || 0));
      case 'price_asc':
        return list.sort((a, b) => (a.price || 0) - (b.price || 0));
      case 'new':
      default:
        return list.sort((a, b) => {
          const ad = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const bd = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return bd - ad;
        });
    }
  }, [products, sort]);

  // keep sort in URL
  useEffect(() => {
    const current = searchParams.get('sort');
    if (current !== sort) {
      const params = new URLSearchParams(searchParams);
      params.set('sort', sort);
      setSearchParams(params, { replace: true });
    }
  }, [sort]);

  const handleSortChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSort(e.target.value);
  };

  const handleAddToCart = (product: Product) => {
    try {
      let imgSrc = product.imageUrl;
      if (!imgSrc && Array.isArray(product.images) && product.images.length > 0 && product.images[0].url) {
        imgSrc = product.images[0].url;
      }
      const cartProduct = {
        id: product._id,
        _id: product._id,
        title: product.title || product.name || 'Unnamed Product',
        name: product.title || product.name || 'Unnamed Product',
        price: product.price || 0,
        imageUrl: imgSrc || '',
        description: product.description || '',
        quantity: 1
      };
      
      addToCart(cartProduct, 1);
      const title = cartProduct.title || cartProduct.name;
      toast.success(`${getTruncatedTitle(title)} added to cart!`, {
        position: 'bottom-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } catch (error) {
      toast.error('Failed to add item to cart', {
        position: 'bottom-right',
        autoClose: 3000,
      });
    }
  };

  return (
    <div className="view-products">
      <Header />
      <div className="view-products-content">
        <div className="sidebar-title-section">
          <h2 className="main-sidebar-title">{categoryName || 'Building Materials'}</h2>
          <div className="sort-dropdown">
            <label htmlFor="sort-select" className="sr-only">Sort</label>
            <select id="sort-select" value={sort} onChange={handleSortChange}>
              <option value="price_desc">Price: High to Low</option>
              <option value="price_asc">Price: Low to High</option>
              <option value="new">Newly Added</option>
            </select>
          </div>
        </div>
        <div className="sidebar-and-cards">
          <ViewAllSidebar subcategories={subcategories} activeSlug={activeSubcategory} />
          <ViewAllCards 
            products={sortedProducts}
            loading={loading}
            error={error}
            onAddToCart={handleAddToCart}
            onMoreInfo={navigateToProduct}
          />
        </div>
      </div>
      <NavigationMobileMenu />
      <FlotingCart /> 
    </div>
  );
};

export default ViewProducts;
